import classNames from 'classnames'
import { observer } from 'mobx-react'
import style from './PdfMarkdown.module.less'

import { commonUtils } from '@/helpers/commonUtils'
import 'katex/dist/katex.min.css'
import React, { Fragment, useEffect, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeClassNames from 'rehype-class-names'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { transformMarkdownString } from '../markdownStringUtil'
// import rehypeMathjax from 'rehype-mathjax'

type ImageData = {
  id: string
  top_left_x: number
  top_left_y: number
  bottom_right_x: number
  bottom_right_y: number
  image_base64: string
}

type PageData = {
  index: number
  markdown: string
  images: ImageData[]
  dimensions: {
    dpi: number
    width: number
    height: number
  }
}

type PdfMarkdownProps = {
  pageData: PageData[]
  imageTranslateMode?: 'manual' | 'auto'
  onImageTranslate?: (imageId: string, imageSrc: string) => void
  visiblePages?: number[] // 可见页面范围
  isLoadingMore?: boolean // 是否正在加载更多
  onPageLoad: (container: HTMLElement) => void
  onImageLoad: (imageId: string) => {
    isTranslated: boolean
    isTranslating: boolean
    translatedSrc: string
  }
}

export const PdfMarkdown = observer((props: PdfMarkdownProps) => {
  const {
    pageData,
    imageTranslateMode = 'manual',
    onImageTranslate,
    visiblePages = [],
    isLoadingMore = false,
    onPageLoad,
    onImageLoad,
  } = props

  // 使用 useMemo 缓存 visiblePageData，避免不必要的重新计算
  const visiblePageData = useMemo(() => {
    return visiblePages.length > 0
      ? pageData.filter((page) => visiblePages.includes(page.index))
      : pageData.slice(0, 20)
  }, [visiblePages, pageData])

  // 使用 useMemo 缓存每个页面的 props，避免传递新的对象引用
  const pageComponents = useMemo(() => {
    return visiblePageData.map((page, index) => {
      const pageProps = {
        page,
        pageData,
        onImageTranslate,
        onPageLoad,
        onImageLoad,
      }

      return <MarkdownPage key={page.index} {...pageProps} />
    })
  }, [visiblePageData, pageData, onImageTranslate, onPageLoad, onImageLoad])

  return (
    <div className={classNames(style.container)} id='pdf-container'>
      <div className={classNames(style.content)} id='pdf-markdown-content'>
        {pageComponents}

        {/* 加载更多提示 */}
        {isLoadingMore && (
          <div className={style.loadingMore}>
            <div className={style.loadingSpinner}></div>
            <span>正在加载更多页面...</span>
          </div>
        )}

        {/* 已加载完所有页面的提示 */}
        {/* {visiblePages.length >= pageData.length && visiblePages.length > 0 && (
          <div className={style.loadingComplete}>
            <span>已加载完所有页面</span>
          </div>
        )} */}
      </div>
    </div>
  )
})

// 将 MarkdownPage 组件用 React.memo 包装，并优化 props 比较
const MarkdownPage = React.memo(
  (props: {
    page: PageData
    pageData: PageData[]
    onImageTranslate?: (imageId: string, imageSrc: string) => void
    onPageLoad: (container: HTMLElement) => void
    onImageLoad: (imageId: string) => {
      isTranslated: boolean
      isTranslating: boolean
      translatedSrc: string
    }
  }) => {
    const { page, pageData, onImageTranslate, onPageLoad, onImageLoad } = props
    const markdown = transformMarkdownString(page.markdown)

    console.log('@@@@page index :', page.index)

    useEffect(() => {
      const container = document.querySelector(`[data-page="${page.index}"]`) as HTMLElement
      const allImgs = container.querySelectorAll('img[id]')
      allImgs.forEach((item) => {
        const img = item as HTMLImageElement
        const id = item.id
        pageData.forEach((page) => {
          const imageData = page.images.find((item) => item.id === id)
          if (imageData && imageData.image_base64) {
            img.src = imageData.image_base64
          }
        })
      })

      onPageLoad(container)
    }, [])

    return (
      <div key={page.index} data-page={page.index} className={style.pageContainer}>
        <ReactMarkdown
          children={markdown}
          remarkPlugins={[remarkMath, remarkGfm]}
          rehypePlugins={[
            // 新增插件：渲染前将所有文本中的 &#36; 替换为 $
            [
              () => (tree: any) => {
                function visit(node: any) {
                  if (node.type === 'text' && typeof node.value === 'string') {
                    node.value = node.value.replace(/&#36;/g, '\\$')
                  }
                  if (node.children) {
                    node.children.forEach(visit)
                  }
                }
                visit(tree)
              },
            ],
            [
              rehypeKatex,
              {
                throwOnError: false,
                strict: false,
              },
            ],
            [rehypeClassNames, { p: style.paragraph }],
          ]}
          components={{
            table: ({ node, ...props }) => {
              return (
                <div className='table-container'>
                  <table {...props} />
                </div>
              )
            },
            td: ({ node, children, ...props }) => {
              // 处理td中的<breakline/>字符串
              const processChildren = (children: any): any => {
                if (typeof children === 'string') {
                  // 如果是字符串，替换<breakline/>为<br/>标签
                  return children.split('<breakline/>').map((part: string, index: number) => (
                    <Fragment key={index}>
                      {part}
                      {index < children.split('<breakline/>').length - 1 && <br />}
                    </Fragment>
                  ))
                }

                if (Array.isArray(children)) {
                  // 如果是数组，递归处理每个元素
                  return children.map((child, index) => (
                    <Fragment key={index}>{processChildren(child)}</Fragment>
                  ))
                }

                return children
              }

              return <td {...props}>{processChildren(children)}</td>
            },
            img: ({ node, ...props }) => {
              const id = props.alt
              // 从 pageData 中查找对应的图片数据获取原始地址
              let imageSrc = props.src || ''
              pageData.forEach((page) => {
                const imageData = page.images.find((item) => item.id === id)
                if (imageData && imageData.image_base64) {
                  imageSrc = imageData.image_base64
                }
              })

              const { isTranslated, isTranslating, translatedSrc } = onImageLoad(id || '')

              console.log('图片信息：', {
                id,
                imageSrc: imageSrc.substring(0, 50) + '...',
                isTranslated,
                isTranslating,
              })

              return (
                <span
                  className={classNames(style.imageContainer, 'math-ignore')}
                  data-image-id={id}
                >
                  <img
                    {...props}
                    id={id}
                    src={isTranslated ? translatedSrc : imageSrc}
                    style={{ display: 'block' }}
                    data-original-src={imageSrc}
                    data-is-translated={isTranslated ? '1' : '0'}
                    className={style.imageMax}
                  />

                  {/* 翻译图标和加载状态 */}
                  <span className={classNames(style.imageOverlay, 'imageOverlay')}>
                    <span
                      className={classNames(style.loadingIcon, 'loadingIcon')}
                      style={{ display: isTranslating ? 'block' : 'none' }}
                    >
                      翻译中...
                    </span>

                    <span
                      title={isTranslated ? '切换为原图' : '翻译图片'}
                      style={{ display: isTranslated ? 'none' : 'flex' }}
                      className={classNames(style.translateIcon, 'translateIcon')}
                      onClick={() => {
                        console.log('点击翻译按钮：', {
                          id,
                          imageSrc: imageSrc.substring(0, 50) + '...',
                          onImageTranslate,
                        })
                        onImageTranslate?.(id || '', imageSrc)
                      }}
                    >
                      <img
                        className='imageIcon'
                        src={commonUtils.getStaticFile(
                          isTranslated ? 'pdf/image-o.png' : 'pdf/image-t.png'
                        )}
                        data-origin={commonUtils.getStaticFile('pdf/image-o.png')}
                        data-target={commonUtils.getStaticFile('pdf/image-t.png')}
                      />
                    </span>
                  </span>
                </span>
              )
            },
            span: ({ node, ...props }) => {
              // console.log('span node :', node, props)

              const className = props.className
              if (className?.includes('katex') && !className?.includes('katex-display')) {
                props.className = props.className?.concat(' math-ignore')
                props.className = props.className?.concat(' math-inline')
                return <span {...props} />
              }

              if (className?.includes('katex-display')) {
                props.className = props.className?.concat(' math-ignore')
                props.className = props.className?.concat(` ${style.mathMaxWidth}`)
              }

              return <span {...props} />
            },
          }}
        />
      </div>
    )
  },
  (prevProps, nextProps) => {
    // 自定义比较函数，只有当关键属性变化时才重新渲染
    return (
      prevProps.page.index === nextProps.page.index &&
      prevProps.page.markdown === nextProps.page.markdown
      // prevProps.onImageTranslate === nextProps.onImageTranslate
      // prevProps.styleValue === nextProps.styleValue &&
      // prevProps.translatedImages === nextProps.translatedImages &&
      // prevProps.translatingImages === nextProps.translatingImages
    )
  }
)
